import {Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';

@Directive({
  selector: '[appPinchZoom]',
  standalone: false,
})
export class PinchZoomDirective implements OnInit, OnDestroy {
  @Input() maxZoom = 5;
  @Input() swipeThreshold = 50;
  /** Scale increment per zoom-button click / wheel notch (desktop zoom). */
  @Input() zoomStep = 0.3;

  @Output() swipeLeft = new EventEmitter<void>();
  @Output() swipeRight = new EventEmitter<void>();

  scale = 1;
  translateX = 0;
  translateY = 0;

  private naturalWidth = 0;
  private naturalHeight = 0;

  private initialDistance = 0;
  private initialScale = 1;
  private isPinching = false;

  private panStartX = 0;
  private panStartY = 0;
  private panStartTranslateX = 0;
  private panStartTranslateY = 0;
  private isPanning = false;

  private swipeStartX = 0;
  private swipeStartTime = 0;

  private transitionTimeout: ReturnType<typeof setTimeout> | null = null;

  private readonly el: HTMLElement;

  private readonly onTouchStart = (e: TouchEvent): void => this.handleTouchStart(e);
  private readonly onTouchMove = (e: TouchEvent): void => this.handleTouchMove(e);
  private readonly onTouchEnd = (e: TouchEvent): void => this.handleTouchEnd(e);
  private readonly onWheel = (e: WheelEvent): void => this.handleWheel(e);

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {
    this.el = this.elementRef.nativeElement;
  }

  ngOnInit(): void {
    this.naturalWidth = this.el.offsetWidth;
    this.naturalHeight = this.el.offsetHeight;
    this.el.addEventListener('touchstart', this.onTouchStart, {passive: false});
    this.el.addEventListener('touchmove', this.onTouchMove, {passive: false});
    this.el.addEventListener('touchend', this.onTouchEnd, {passive: false});
    // Non-passive so wheel zoom can preventDefault the page scroll.
    this.el.addEventListener('wheel', this.onWheel, {passive: false});
  }

  ngOnDestroy(): void {
    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout);
    }
    this.el.removeEventListener('touchstart', this.onTouchStart);
    this.el.removeEventListener('touchmove', this.onTouchMove);
    this.el.removeEventListener('touchend', this.onTouchEnd);
    this.el.removeEventListener('wheel', this.onWheel);
  }

  resetTransform(): void {
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.applyTransform();
  }

  /** Desktop zoom entry points (zoom buttons). */
  zoomIn(): void { this.setScale(this.scale + this.zoomStep); }
  zoomOut(): void { this.setScale(this.scale - this.zoomStep); }

  /** Clamp to [1, maxZoom]; snap pan back to centre when fully zoomed out. */
  private setScale(target: number): void {
    this.scale = Math.min(Math.max(target, 1), this.maxZoom);
    if (this.scale === 1) {
      this.translateX = 0;
      this.translateY = 0;
    } else {
      this.clampTranslation();
    }
    this.applyTransform();
  }

  private handleWheel(e: WheelEvent): void {
    e.preventDefault();
    this.setScale(this.scale + (e.deltaY < 0 ? this.zoomStep : -this.zoomStep));
  }

  private handleTouchStart(e: TouchEvent): void {
    if (e.touches.length === 2) {
      e.preventDefault();
      this.isPinching = true;
      this.isPanning = false;
      this.initialDistance = this.getDistance(e.touches[0], e.touches[1]);
      this.initialScale = this.scale;
    } else if (e.touches.length === 1) {
      this.isPanning = true;
      this.panStartX = e.touches[0].clientX;
      this.panStartY = e.touches[0].clientY;
      this.panStartTranslateX = this.translateX;
      this.panStartTranslateY = this.translateY;
      this.swipeStartX = e.touches[0].clientX;
      this.swipeStartTime = Date.now();
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    if (e.touches.length === 2 && this.isPinching) {
      e.preventDefault();
      const currentDistance = this.getDistance(e.touches[0], e.touches[1]);
      const ratio = currentDistance / this.initialDistance;
      this.scale = Math.min(Math.max(this.initialScale * ratio, 1), this.maxZoom);
      this.clampTranslation();
      this.applyTransform();
    } else if (e.touches.length === 1 && this.scale > 1) {
      e.preventDefault();
      if (!this.isPanning) {
        // Transitioning from pinch to pan (one finger lifted)
        this.isPanning = true;
        this.panStartX = e.touches[0].clientX;
        this.panStartY = e.touches[0].clientY;
        this.panStartTranslateX = this.translateX;
        this.panStartTranslateY = this.translateY;
      }
      const dx = e.touches[0].clientX - this.panStartX;
      const dy = e.touches[0].clientY - this.panStartY;
      this.translateX = this.panStartTranslateX + dx;
      this.translateY = this.panStartTranslateY + dy;
      this.clampTranslation();
      this.applyTransform();
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (this.isPinching && e.touches.length < 2) {
      this.isPinching = false;

      if (this.scale < 1.1) {
        this.el.style.transition = 'transform 200ms ease-out';
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.applyTransform();
        this.transitionTimeout = setTimeout(() => {
          this.el.style.transition = '';
        }, 200);
      }

      // Transition to pan if one finger remains
      if (e.touches.length === 1) {
        this.isPanning = true;
        this.panStartX = e.touches[0].clientX;
        this.panStartY = e.touches[0].clientY;
        this.panStartTranslateX = this.translateX;
        this.panStartTranslateY = this.translateY;
      }
      return;
    }

    if (e.touches.length === 0 && this.isPanning) {
      this.isPanning = false;

      // Swipe detection
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - this.swipeStartX;
      const elapsed = Math.max(Date.now() - this.swipeStartTime, 1);
      const velocity = Math.abs(deltaX) / elapsed;

      if (Math.abs(deltaX) > this.swipeThreshold && velocity > 0.3) {
        if (this.scale === 1) {
          if (deltaX < 0) {
            this.swipeLeft.emit();
          } else {
            this.swipeRight.emit();
          }
        } else {
          // When zoomed, only swipe at horizontal edge
          const maxTranslateX = this.getMaxTranslateX();
          if (deltaX < 0 && this.translateX <= -maxTranslateX) {
            this.swipeLeft.emit();
          } else if (deltaX > 0 && this.translateX >= maxTranslateX) {
            this.swipeRight.emit();
          }
        }
      }
    }
  }

  private getDistance(t1: Touch, t2: Touch): number {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private getMaxTranslateX(): number {
    return this.naturalWidth * (this.scale - 1) / 2;
  }

  private getMaxTranslateY(): number {
    return this.naturalHeight * (this.scale - 1) / 2;
  }

  private clampTranslation(): void {
    const maxX = this.getMaxTranslateX();
    const maxY = this.getMaxTranslateY();
    this.translateX = Math.min(Math.max(this.translateX, -maxX), maxX);
    this.translateY = Math.min(Math.max(this.translateY, -maxY), maxY);
  }

  private applyTransform(): void {
    this.el.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
  }
}
